import { useState } from "react";
import { supabase } from '../utils/supabaseClient'; // ✅ Import Supabase

function Login({ onLogin }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("L3");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ✅ Valid credentials with email mapping for Supabase
  const validUsers = {
    L1: { 
      username: "admin", 
      password: "1234",
      email: "admin@test.com"
    },
    L2: { 
      username: "compliance", 
      password: "1234",
      email: "compliance@test.com"
    },
    L3: { 
      username: "operation", 
      password: "1234",
      email: "operation@test.com"
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (isSubmitting) return;

    setError("");
    setIsSubmitting(true);

    try {
      if (!username || !password) {
        setError("Please enter both username and password.");
        setIsSubmitting(false);
        return;
      }

      // Validate credentials
      const validUser = validUsers[role];
      if (username !== validUser.username || password !== validUser.password) {
        setError("Invalid username, password, or role combination. Please try again.");
        setIsSubmitting(false);
        return;
      }

      console.log(`🔐 Attempting Supabase login for ${validUser.email}...`);

      // ✅ Login with Supabase using the mapped email
      const { data, error: authError } = await supabase.auth.signInWithPassword({
        email: validUser.email,
        password: validUser.password
      });

      if (authError) {
        console.error('❌ Supabase auth error:', authError);
        setError(`Login failed: ${authError.message}`);
        setIsSubmitting(false);
        return;
      }

      // If Supabase returned a session and user, great — proceed.
      if (data && data.session && data.user) {
        console.log('✅ Supabase login returned session and user directly.');
        // Still double-check session is accessible
        const { data: { session: verifySession } } = await supabase.auth.getSession();
        console.log('🔍 Session verification (immediate):', !!verifySession);

        if (verifySession) {
          // Normal fast path
          setIsSubmitting(false);
          onLogin(username, role);
          console.log(`User ${username} logged in as ${role}`);
          return;
        }
        // If verifySession is falsy fallthrough to event-listener fallback below
      }

      // If we reached here, either data.session was missing or getSession() returned falsy.
      console.warn('⚠️ Session not immediately accessible - waiting for auth state to settle (fallback).');

      // Subscribe to auth state change and wait for SIGNED_IN
      const { data: listener } = supabase.auth.onAuthStateChange((event, session) => {
        console.log('Auth event:', event, 'session?', !!session);
        if (event === 'SIGNED_IN' && session) {
          try {
            // clean up subscription
            if (listener && listener.subscription) {
              listener.subscription.unsubscribe();
            }
          } catch (cleanupErr) {
            console.warn('Cleanup error:', cleanupErr);
          }
          setIsSubmitting(false);
          onLogin(username, role);
          console.log(`User ${username} logged in as ${role} (via auth event)`);
        } else if (event === 'SIGNED_OUT') {
          // unexpected — cleanup and show error
          try {
            if (listener && listener.subscription) {
              listener.subscription.unsubscribe();
            }
          } catch (cleanupErr) {
            console.warn('Cleanup error:', cleanupErr);
          }
          setIsSubmitting(false);
          setError('Login did not complete. Please try again.');
        }
      });

      // safety fallback: if no auth event in 6 seconds, unsubscribe and show an error
      setTimeout(() => {
        try {
          if (listener && listener.subscription) {
            // if still subscribed, unsubscribe and show message (but don't override a successful login)
            listener.subscription.unsubscribe();
            setIsSubmitting(false);
            if (!supabase.auth.getUser) {
              // no-op, kept for safety; real session check already attempted earlier
            }
            // Only set error if there is no session
            supabase.auth.getSession().then(({ data: { session } }) => {
              if (!session) {
                setError('Login timed out. Please try again.');
              }
            }).catch(() => {
              setError('Login timed out. Please try again.');
            });
          }
        } catch (err) {
          console.warn('Timeout cleanup error:', err);
        }
      }, 6000);

    } catch (error) {
      console.error('Login error:', error);
      setError(`Login failed: ${error?.message || error}`);
      setIsSubmitting(false);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        background: "#f4f6f8",
        fontFamily: "Arial, sans-serif",
        padding: 20,
      }}
    >
      <form
        onSubmit={handleLogin}
        style={{
          background: "white",
          padding: 40,
          borderRadius: 10,
          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
          width: 350,
          textAlign: "center",
        }}
      >
        <div style={{ marginBottom: 25 }}>
          <img
            src={`${process.env.PUBLIC_URL}/Company_logo.jpg`}
            alt="GNXi Logo"
            style={{
              height: 60,
              width: "auto",
              marginBottom: 10,
              objectFit: "contain",
              display: "block",
              marginLeft: "auto",
              marginRight: "auto",
            }}
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = "https://via.placeholder.com/100x60?text=Logo";
            }}
          />
          <h1 style={{ color: "#0d47a1", marginBottom: 0 }}>AMLi</h1>
        </div>

        <input
          type="text"
          placeholder="Username"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          disabled={isSubmitting}
          style={{
            width: "100%",
            padding: 12,
            marginBottom: 15,
            borderRadius: 5,
            border: "1px solid #ccc",
            fontSize: 14,
          }}
        />

        <input
          type="password"
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          disabled={isSubmitting}
          style={{
            width: "100%",
            padding: 12,
            marginBottom: 15,
            borderRadius: 5,
            border: "1px solid #ccc",
            fontSize: 14,
          }}
        />

        <div style={{ marginBottom: 20, textAlign: "left" }}>
          <label style={{ display: "block", marginBottom: 8, fontWeight: "bold" }}>
            Select Role:
          </label>

          <label style={{ display: "block", marginBottom: 5 }}>
            <input
              type="radio"
              name="role"
              value="L1"
              checked={role === "L1"}
              onChange={(e) => setRole(e.target.value)}
              disabled={isSubmitting}
            />{" "}
            L1 - Admin
          </label>

          <label style={{ display: "block", marginBottom: 5 }}>
            <input
              type="radio"
              name="role"
              value="L2"
              checked={role === "L2"}
              onChange={(e) => setRole(e.target.value)}
              disabled={isSubmitting}
            />{" "}
            L2 - Compliance Officer
          </label>

          <label style={{ display: "block", marginBottom: 5 }}>
            <input
              type="radio"
              name="role"
              value="L3"
              checked={role === "L3"}
              onChange={(e) => setRole(e.target.value)}
              disabled={isSubmitting}
            />{" "}
            L3 - Operations Team / Customer Service
          </label>
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          style={{
            width: "100%",
            padding: 12,
            backgroundColor: isSubmitting ? "#ccc" : "#1976d2",
            color: "white",
            border: "none",
            borderRadius: 5,
            cursor: isSubmitting ? "not-allowed" : "pointer",
            fontSize: 16,
            fontWeight: "bold",
          }}
        >
          {isSubmitting ? "Logging in..." : "Login"}
        </button>

        {error && (
          <p style={{ color: "red", marginTop: 15, fontSize: 14 }}>{error}</p>
        )}
      </form>
    </div>
  );
}

export default Login;
